import React, { useState, useEffect } from 'react';
import api, { products as productsAPI, sales, branches } from '../services/api';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Chip,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  LinearProgress,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  CardActions,
  Divider,
  Stack,
  Collapse,
} from '@mui/material';
import {
  Search as SearchIcon,
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Inventory as InventoryIcon,
  SwapHoriz as TransferIcon,
  Remove as RemoveIcon,
  Add as AddIcon,
  Clear as ClearIcon,
  ShoppingCart as ShoppingCartIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import { brands as brandsAPI, categories as categoriesAPI } from '../services/api';
import { getBrandColor, getBrandLogo, getBrandTextColor } from '../utils/brandUtils';
import { useAuth } from '../services/auth';

function Inventory() {
  const { canEdit, isViewer } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Filter state
  const [selectedBrand, setSelectedBrand] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [total, setTotal] = useState(0);

  // Transfer state
  const [transferring, setTransferring] = useState({});
  const [updating, setUpdating] = useState({});

  // Debounce state to prevent rapid clicking
  const [lastClickTime, setLastClickTime] = useState({});

  // Sale state
  const [selling, setSelling] = useState({});

  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    onConfirm: null,
    onCancel: null
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [showFilters, setShowFilters] = useState(false);

  // Helper function to show confirmation dialog
  const showConfirmDialog = (title, message, onConfirm) => {
    setConfirmDialog({
      open: true,
      title,
      message,
      onConfirm,
      onCancel: () => setConfirmDialog(prev => ({ ...prev, open: false }))
    });
  };

  // Helper function to get unit price from product pricing array
  const getProductUnitPrice = (product) => {
    if (product.Pricing && product.Pricing.length > 0) {
      // Find the active pricing (IsActive: true) or use the first one
      const activePricing = product.Pricing.find(p => p.IsActive) || product.Pricing[0];
      // Use retail price if available, otherwise use wholesale price
      return activePricing.RetailPrice || activePricing.WholesalePrice || 0;
    }
    return 0;
  };

  useEffect(() => {
    fetchProducts();
  }, [page, rowsPerPage, searchTerm, selectedBrand, selectedCategory]);

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);

      // Build query parameters for filtered products
      const params = new URLSearchParams({
        limit: rowsPerPage.toString(),
        offset: (page * rowsPerPage).toString(),
      });

      if (searchTerm.trim()) {
        params.append('search', searchTerm.trim());
      }
      if (selectedBrand) {
        params.append('brand', selectedBrand);
      }
      if (selectedCategory) {
        params.append('part_category', selectedCategory);
      }

      const response = await api.get(`/api/v1/products/filtered?${params}`);
      const data = response.data;

      // Products now include pricing and stock data from the single API call
      setProducts(data.Products || []);
      setTotal(data.Total || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setPage(0); // Reset to first page when searching
  };

  const fetchFilterOptions = async () => {
    try {
      const [brandsRes, categoriesRes] = await Promise.all([
        brandsAPI.getLite(),
        categoriesAPI.getLite(),
      ]);

      setBrands(brandsRes.data.Brands || []);
      setCategories(categoriesRes.data.Categories || []);
    } catch (error) {
      console.error('Failed to fetch filter options:', error);
    }
  };

  const clearFilters = () => {
    setSelectedBrand('');
    setSelectedCategory('');
    setSearchTerm('');
    setPage(0);
  };

  // Transfer stock between branches
  const handleTransferStock = async (productId, fromBranch, toBranch, Quantity) => {
    // Validation: Check for valid parameters
    if (!productId || !fromBranch || !toBranch || !Quantity || Quantity <= 0) {
      setError('Invalid transfer parameters - missing product, branches, or quantity');
      setTimeout(() => setError(null), 3000);
      return;
    }

    // Validation: Check if transferring to the same branch
    if (fromBranch === toBranch) {
      setError('Cannot transfer to the same branch');
      setTimeout(() => setError(null), 3000);
      return;
    }

    // Validation: Check if source branch has enough stock
    const product = products.find(p => p.Id === productId);
    const sourceStock = product?.Stock?.find(s => s.BranchName === fromBranch);
    if (!sourceStock || sourceStock.Quantity < Quantity) {
      setError(`Insufficient stock in ${fromBranch} for transfer (Available: ${sourceStock?.Quantity || 0}, Requested: ${Quantity})`);
      setTimeout(() => setError(null), 5000);
      return;
    }

    const transferKey = `${productId}-${fromBranch}-${toBranch}`;
    const now = Date.now();

    // Debounce: prevent multiple clicks within 1 second
    if (lastClickTime[transferKey] && (now - lastClickTime[transferKey]) < 1000) {
      return;
    }
    setLastClickTime(prev => ({ ...prev, [transferKey]: now }));

    setTransferring(prev => ({ ...prev, [transferKey]: true }));

    try {
      await productsAPI.transferStock(productId, {
        FromBranch: fromBranch,
        ToBranch: toBranch,
        Quantity: Quantity,
      });

      // Refresh the products data
      await fetchProducts();

      // Clear any previous errors
      setError(null);
    } catch (err) {
      console.error('Stock transfer error:', err);
      setError(`Failed to transfer from ${fromBranch} to ${toBranch}: ${err.message}`);

      // Clear error after 5 seconds
      setTimeout(() => setError(null), 5000);
    } finally {
      setTransferring(prev => ({ ...prev, [transferKey]: false }));
    }
  };

  // Update stock (subtract for sale/use)
  const handleUpdateStock = async (productId, branchName, currentQuantity, changeAmount) => {
    // Validation: Check if trying to subtract more than available
    if (changeAmount < 0 && currentQuantity <= 0) {
      setError(`Cannot subtract - no stock available in ${branchName}`);
      setTimeout(() => setError(null), 3000);
      return;
    }

    // Validation: Check for valid parameters
    if (!productId || !branchName || typeof currentQuantity !== 'number' || typeof changeAmount !== 'number') {
      setError('Invalid parameters for stock update');
      setTimeout(() => setError(null), 3000);
      return;
    }

    const updateKey = `${productId}-${branchName}`;
    const now = Date.now();

    // Debounce: prevent multiple clicks within 1 second
    if (lastClickTime[updateKey] && (now - lastClickTime[updateKey]) < 1000) {
      return;
    }
    setLastClickTime(prev => ({ ...prev, [updateKey]: now }));

    const newQuantity = Math.max(0, currentQuantity + changeAmount);

    // Validation: Warn if adding too much stock
    if (changeAmount > 0 && newQuantity > 1000) {
      setError(`Warning: Stock quantity (${newQuantity}) is very high. Consider checking if this is correct.`);
      setTimeout(() => setError(null), 5000);
    }

    setUpdating(prev => ({ ...prev, [updateKey]: true }));

    try {
      await productsAPI.updateStock(productId, branchName, {
        Quantity: newQuantity,
        MinStockLevel: 0, // Default to 0 for quick actions
        MaxStockLevel: 1000, // Default to 1000 for quick actions
      });

      // Refresh the products data
      await fetchProducts();

      // Clear any previous errors
      setError(null);
    } catch (err) {
      console.error('Stock update error:', err);
      setError(`Failed to update ${branchName} stock: ${err.message}`);

      // Clear error after 5 seconds
      setTimeout(() => setError(null), 5000);
    } finally {
      setUpdating(prev => ({ ...prev, [updateKey]: false }));
    }
  };

  // Handle sales (record sale and automatically reduce stock)
  const handleSaleStock = async (productId, branchName, currentQuantity, product) => {
    // Validation: Check for valid parameters
    if (!productId || !branchName || !product) {
      setError('Invalid sale parameters - missing product or branch information');
      setTimeout(() => setError(null), 3000);
      return;
    }

    // Validation: Check stock availability
    if (currentQuantity <= 0) {
      setError(`Cannot sell - no stock available in ${branchName}`);
      setTimeout(() => setError(null), 3000);
      return;
    }

    // Validation: Check if product has required information
    if (!product.Brand || !product.Model) {
      setError('Cannot sell - product information incomplete (missing brand or model)');
      setTimeout(() => setError(null), 3000);
      return;
    }

    // Calculate unit price using helper function
    const unitPrice = getProductUnitPrice(product);

    // Validation: Check pricing
    if (unitPrice <= 0) {
      setError('Cannot sell - no valid price set for this product');
      setTimeout(() => setError(null), 3000);
      return;
    }

    // Validation: Check for reasonable price (not too high)
    if (unitPrice > 100000) {
      setError(`Warning: Very high unit price (₱${unitPrice.toLocaleString()}). Please verify this is correct.`);
      setTimeout(() => setError(null), 5000);
      return;
    }

    const saleKey = `${productId}-${branchName}`;
    const now = Date.now();

    // Debounce: prevent multiple clicks within 1 second
    if (lastClickTime[saleKey] && (now - lastClickTime[saleKey]) < 1000) {
      return;
    }
    setLastClickTime(prev => ({ ...prev, [saleKey]: now }));

    setSelling(prev => ({ ...prev, [saleKey]: true }));

    try {
      // Get branch ID
      const branchResponse = await branches.getAll();
      const branchData = branchResponse.data;
      const branch = branchData.Branches?.find(b => b.Name === branchName);

      if (!branch) {
        throw new Error('Branch not found');
      }

      // Create sale record
      await sales.create({
        ProductId: productId,
        BranchId: branch.Id,
        Quantity: 1,
        UnitPrice: unitPrice,
        CustomerInfo: 'Walk-in Customer',
        Notes: 'Quick sale from inventory'
      });

      // Refresh the products data to show updated stock
      await fetchProducts();

      // Clear any previous errors
      setError(null);
    } catch (err) {
      console.error('Sale error:', err);
      setError(`Failed to record sale: ${err.message}`);

      // Clear error after 5 seconds
      setTimeout(() => setError(null), 5000);
    } finally {
      setSelling(prev => ({ ...prev, [saleKey]: false }));
    }
  };

  // Confirmation wrapper functions
  const confirmUpdateStock = (productId, branchName, currentQuantity, changeAmount, productName) => {
    const action = changeAmount > 0 ? 'add' : 'subtract';
    const actionText = changeAmount > 0 ? `Add ${changeAmount}` : `Subtract ${Math.abs(changeAmount)}`;
    const currentText = currentQuantity > 0 ? ` (Current: ${currentQuantity})` : ' (Currently out of stock)';

    showConfirmDialog(
      `Confirm Stock ${action === 'add' ? 'Addition' : 'Subtraction'}`,
      `Are you sure you want to ${actionText.toLowerCase()} stock for "${productName}" in ${branchName}?${currentText}`,
      () => {
        setConfirmDialog(prev => ({ ...prev, open: false }));
        handleUpdateStock(productId, branchName, currentQuantity, changeAmount);
      }
    );
  };

  const confirmTransferStock = (productId, fromBranch, toBranch, quantity, productName) => {
    showConfirmDialog(
      'Confirm Stock Transfer',
      `Are you sure you want to transfer ${quantity} unit(s) of "${productName}" from ${fromBranch} to ${toBranch}?`,
      () => {
        setConfirmDialog(prev => ({ ...prev, open: false }));
        handleTransferStock(productId, fromBranch, toBranch, quantity);
      }
    );
  };

  const confirmSaleStock = (productId, branchName, currentQuantity, product) => {
    const unitPrice = getProductUnitPrice(product);
    const productName = `${product.Brand} ${product.Model}`;

    showConfirmDialog(
      'Confirm Sale',
      `Are you sure you want to record a sale for "${productName}" from ${branchName}? This will reduce stock by 1 unit and record a sale of ₱${unitPrice.toLocaleString()}.`,
      () => {
        setConfirmDialog(prev => ({ ...prev, open: false }));
        handleSaleStock(productId, branchName, currentQuantity, product);
      }
    );
  };


  const getStockStatus = (stock) => {
    if (!stock || stock.length === 0) {
      return { status: 'No Stock', color: theme.palette.error.main, severity: 'high' };
    }

    const totalStock = stock.reduce((total, s) => total + s.Quantity, 0);
    const hasLowStock = stock.some(s => s.Quantity <= s.MinStockLevel && s.MinStockLevel > 0);

    if (totalStock === 0) {
      return { status: 'Out of Stock', color: theme.palette.error.main, severity: 'high' };
    } else if (hasLowStock) {
      return { status: 'Low Stock', color: theme.palette.warning.main, severity: 'medium' };
    } else {
      return { status: 'In Stock', color: theme.palette.success.main, severity: 'low' };
    }
  };

  const getStockProgress = (current, min, max) => {
    if (max === 0) return 0;
    return Math.min((current / max) * 100, 100);
  };

  const formatPrice = (price) => {
    if (!price) return '-';
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP',
    }).format(price);
  };

  // Mobile Inventory Card Component
  const InventoryCard = ({ product }) => {
    const stockStatus = getStockStatus(product.Stock);
    const mjtechStock = product.Stock?.find(s => s.BranchName === 'MJTech');
    const mobileCareStock = product.Stock?.find(s => s.BranchName === 'Mobile Care');
    const totalStock = product.Stock?.reduce((total, s) => total + s.Quantity, 0) || 0;
    const unitPrice = getProductUnitPrice(product);

    return (
      <Card
        sx={{
          mb: 2,
          background: 'linear-gradient(145deg, #1A1F2E 0%, #2A2F3E 100%)',
          border: '1px solid rgba(0, 191, 255, 0.2)',
          '&:hover': {
            border: '1px solid rgba(0, 191, 255, 0.4)',
            transform: 'translateY(-2px)',
          },
          transition: 'all 0.3s ease-in-out',
        }}
      >
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Avatar
              sx={{
                width: 32,
                height: 32,
                mr: 2,
                backgroundColor: getBrandColor(product.Brand),
                color: getBrandTextColor(product.Brand),
                fontSize: '0.75rem',
                fontWeight: 600,
              }}
            >
              {getBrandLogo(product.Brand)}
            </Avatar>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="subtitle1" fontWeight={600} noWrap>
                {product.Brand} {product.Model}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                {product.Brand && (
                  <Chip
                    label={product.Brand}
                    size="small"
                    sx={{
                      backgroundColor: `${getBrandColor(product.Brand)}40`,
                      color: getBrandTextColor(product.Brand),
                      fontWeight: 600,
                      border: `1px solid ${getBrandColor(product.Brand)}60`,
                      height: 20,
                      fontSize: '0.7rem',
                    }}
                  />
                )}
                <Chip
                  label={product.PartCategory || 'Other'}
                  size="small"
                  color="secondary"
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
                <Chip
                  label={stockStatus.status}
                  size="small"
                  sx={{
                    backgroundColor: `${stockStatus.color}20`,
                    color: stockStatus.color,
                    fontWeight: 600,
                    border: `1px solid ${stockStatus.color}60`,
                    height: 20,
                    fontSize: '0.7rem',
                  }}
                />
              </Box>
            </Box>
          </Box>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6}>
              <Typography variant="caption" color="textSecondary">
                MJTech Stock
              </Typography>
              <Typography variant="h6" fontWeight={600} color="primary">
                {mjtechStock?.Quantity || 0}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="textSecondary">
                Mobile Care Stock
              </Typography>
              <Typography variant="h6" fontWeight={600} color="secondary">
                {mobileCareStock?.Quantity || 0}
              </Typography>
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="body2" color="textSecondary">
              Total Stock: {totalStock} units
            </Typography>
            <Typography variant="body2" color="primary" fontWeight={600}>
              Unit Price: {formatPrice(unitPrice)}
            </Typography>
          </Box>

          {!isViewer() && canEdit() && (
            <Box>
              <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                Quick Actions
              </Typography>
              <Grid container spacing={1}>
                {/* MJTech Actions */}
                <Grid item xs={6}>
                  <Typography variant="caption" color="primary" sx={{ display: 'block', mb: 0.5 }}>
                    MJTech
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {mjtechStock && mjtechStock.Quantity > 0 && (
                      <IconButton
                        size="small"
                        onClick={() => confirmUpdateStock(product.Id, 'MJTech', mjtechStock.Quantity, -1, `${product.Brand} ${product.Model}`)}
                        disabled={updating[`${product.Id}-MJTech`]}
                        sx={{
                          backgroundColor: 'error.main',
                          color: '#1a237e',
                          width: 28,
                          height: 28,
                          borderRadius: 1.5,
                          '&:hover': {
                            backgroundColor: 'error.dark',
                            transform: 'scale(1.05)',
                          },
                        }}
                      >
                        <RemoveIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    )}
                    <IconButton
                      size="small"
                      onClick={() => confirmUpdateStock(product.Id, 'MJTech', mjtechStock?.Quantity || 0, 1, `${product.Brand} ${product.Model}`)}
                      disabled={updating[`${product.Id}-MJTech`]}
                      sx={{
                        backgroundColor: 'success.main',
                        color: '#1a237e',
                        width: 28,
                        height: 28,
                        borderRadius: 1.5,
                        '&:hover': {
                          backgroundColor: 'success.dark',
                          transform: 'scale(1.05)',
                        },
                      }}
                    >
                      <AddIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                    {mjtechStock && mjtechStock.Quantity > 0 && (
                      <>
                        <IconButton
                          size="small"
                          onClick={() => confirmTransferStock(product.Id, 'MJTech', 'Mobile Care', 1, `${product.Brand} ${product.Model}`)}
                          disabled={transferring[`${product.Id}-MJTech-Mobile Care`]}
                          sx={{
                            backgroundColor: 'info.main',
                            color: '#1a237e',
                            width: 28,
                            height: 28,
                            borderRadius: 1.5,
                            '&:hover': {
                              backgroundColor: 'info.dark',
                              transform: 'scale(1.05)',
                            },
                          }}
                        >
                          <TransferIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => confirmSaleStock(product.Id, 'MJTech', mjtechStock.Quantity, product)}
                          disabled={selling[`${product.Id}-MJTech`]}
                          sx={{
                            backgroundColor: 'warning.main',
                            color: '#1a237e',
                            width: 28,
                            height: 28,
                            borderRadius: 1.5,
                            '&:hover': {
                              backgroundColor: 'warning.dark',
                              transform: 'scale(1.05)',
                            },
                          }}
                        >
                          <ShoppingCartIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </>
                    )}
                  </Box>
                </Grid>
                {/* Mobile Care Actions */}
                <Grid item xs={6}>
                  <Typography variant="caption" color="secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Mobile Care
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {mobileCareStock && mobileCareStock.Quantity > 0 && (
                      <IconButton
                        size="small"
                        onClick={() => confirmUpdateStock(product.Id, 'Mobile Care', mobileCareStock.Quantity, -1, `${product.Brand} ${product.Model}`)}
                        disabled={updating[`${product.Id}-Mobile Care`]}
                        sx={{
                          backgroundColor: 'error.main',
                          color: '#1a237e',
                          width: 28,
                          height: 28,
                          borderRadius: 1.5,
                          '&:hover': {
                            backgroundColor: 'error.dark',
                            transform: 'scale(1.05)',
                          },
                        }}
                      >
                        <RemoveIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    )}
                    <IconButton
                      size="small"
                      onClick={() => confirmUpdateStock(product.Id, 'Mobile Care', mobileCareStock?.Quantity || 0, 1, `${product.Brand} ${product.Model}`)}
                      disabled={updating[`${product.Id}-Mobile Care`]}
                      sx={{
                        backgroundColor: 'success.main',
                        color: '#1a237e',
                        width: 28,
                        height: 28,
                        borderRadius: 1.5,
                        '&:hover': {
                          backgroundColor: 'success.dark',
                          transform: 'scale(1.05)',
                        },
                      }}
                    >
                      <AddIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                    {mobileCareStock && mobileCareStock.Quantity > 0 && (
                      <>
                        <IconButton
                          size="small"
                          onClick={() => confirmTransferStock(product.Id, 'Mobile Care', 'MJTech', 1, `${product.Brand} ${product.Model}`)}
                          disabled={transferring[`${product.Id}-Mobile Care-MJTech`]}
                          sx={{
                            backgroundColor: 'secondary.main',
                            color: '#1a237e',
                            width: 28,
                            height: 28,
                            borderRadius: 1.5,
                            '&:hover': {
                              backgroundColor: 'secondary.dark',
                              transform: 'scale(1.05)',
                            },
                          }}
                        >
                          <TransferIcon sx={{ fontSize: 14, transform: 'scaleX(-1)' }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => confirmSaleStock(product.Id, 'Mobile Care', mobileCareStock.Quantity, product)}
                          disabled={selling[`${product.Id}-Mobile Care`]}
                          sx={{
                            backgroundColor: 'warning.main',
                            color: '#1a237e',
                            width: 28,
                            height: 28,
                            borderRadius: 1.5,
                            '&:hover': {
                              backgroundColor: 'warning.dark',
                              transform: 'scale(1.05)',
                            },
                          }}
                        >
                          <ShoppingCartIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 700,
            background: 'linear-gradient(45deg, #00BFFF 30%, #0080FF 90%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 1,
          }}
        >
          Inventory Management
        </Typography>
        <Typography variant="subtitle1" color="textSecondary">
          Monitor stock levels across all branches
        </Typography>
      </Box>

      {/* Search and Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={isMobile ? 10 : 6}>
              <TextField
                fullWidth
                placeholder="Search inventory items..."
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  endAdornment: isMobile && (
                    <IconButton
                      onClick={() => setShowFilters(!showFilters)}
                      size="small"
                      sx={{ color: showFilters ? 'primary.main' : 'text.secondary' }}
                    >
                      <FilterIcon />
                    </IconButton>
                  ),
                }}
              />
            </Grid>
            {isMobile && (
              <Grid item xs={2}>
                <Tooltip title="Clear all filters">
                  <IconButton onClick={clearFilters} size="large">
                    <ClearIcon />
                  </IconButton>
                </Tooltip>
              </Grid>
            )}
            {!isMobile && (
              <>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Brand</InputLabel>
                    <Select
                      value={selectedBrand}
                      label="Brand"
                      onChange={(e) => setSelectedBrand(e.target.value)}
                    >
                      <MenuItem value="">All Brands</MenuItem>
                      {brands.map((brand) => (
                        <MenuItem key={brand} value={brand}>
                          {brand}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <FormControl fullWidth>
                      <InputLabel>Category</InputLabel>
                      <Select
                        value={selectedCategory}
                        label="Category"
                        onChange={(e) => setSelectedCategory(e.target.value)}
                      >
                        <MenuItem value="">All Categories</MenuItem>
                        {categories.map((category) => (
                          <MenuItem key={category} value={category}>
                            {category}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Tooltip title="Clear all filters">
                      <IconButton onClick={clearFilters} size="large">
                        <ClearIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Grid>
              </>
            )}
            <Collapse in={isMobile && showFilters} sx={{ width: '100%' }}>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Brand</InputLabel>
                    <Select
                      value={selectedBrand}
                      label="Brand"
                      onChange={(e) => setSelectedBrand(e.target.value)}
                    >
                      <MenuItem value="">All Brands</MenuItem>
                      {brands.map((brand) => (
                        <MenuItem key={brand} value={brand}>
                          {brand}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={selectedCategory}
                      label="Category"
                      onChange={(e) => setSelectedCategory(e.target.value)}
                    >
                      <MenuItem value="">All Categories</MenuItem>
                      {categories.map((category) => (
                        <MenuItem key={category} value={category}>
                          {category}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Collapse>
          </Grid>
        </CardContent>
      </Card>

      {/* Inventory Display - Responsive */}
      {loading ? (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Loading inventory...</Typography>
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ m: 2 }}>
          {error}
        </Alert>
      ) : (
        <>
          {/* Mobile Card View */}
          {isMobile ? (
            <Box>
              {products.map((product) => (
                <InventoryCard key={product.Id} product={product} />
              ))}
            </Box>
          ) : (
            /* Desktop Table View */
            <Card>
              <CardContent sx={{ p: 0 }}>
                <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Product</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="center">MJTech Stock</TableCell>
                      <TableCell align="center">Mobile Care Stock</TableCell>
                      <TableCell align="center">Total Stock</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {products.map((product) => {
                        const stockStatus = getStockStatus(product.Stock);
                        const mjtechStock = product.Stock?.find(s => s.BranchName === 'MJTech');
                        const mobileCareStock = product.Stock?.find(s => s.BranchName === 'Mobile Care');
                        const totalStock = product.Stock?.reduce((total, s) => total + s.Quantity, 0) || 0;

                        return (
                          <TableRow key={product.Id} hover>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Avatar
                                  sx={{
                                    width: 40,
                                    height: 40,
                                    mr: 2,
                                    backgroundColor: getBrandColor(product.Brand),
                                    color: getBrandTextColor(product.Brand),
                                    fontSize: '0.875rem',
                                    fontWeight: 600,
                                  }}
                                >
                                  {getBrandLogo(product.Brand)}
                                </Avatar>
                                <Box>
                                  <Typography variant="subtitle2" fontWeight={600}>
                                    {`${product.Brand || ''} ${product.Model || ''}`.trim()}
                                  </Typography>
                                  <Typography variant="caption" color="textSecondary">
                                    {product.PartCategory}
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={stockStatus.status}
                                size="small"
                                sx={{
                                  backgroundColor: `${stockStatus.color}20`,
                                  color: stockStatus.color,
                                  fontWeight: 600,
                                }}
                                icon={stockStatus.severity === 'high' ? <WarningIcon /> :
                                      stockStatus.severity === 'medium' ? <TrendingDownIcon /> :
                                      <TrendingUpIcon />}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Box sx={{ minWidth: 120, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                <Typography variant="h6" fontWeight={600}>
                                  {mjtechStock?.Quantity || 0}
                                </Typography>
                                {/* MJTech Stock Actions */}
                                {canEdit() ? (
                                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                    {mjtechStock && mjtechStock.Quantity > 0 && (
                                      <Tooltip title="Subtract 1">
                                        <IconButton
                                          size="small"
                                          onClick={() => confirmUpdateStock(product.Id, 'MJTech', mjtechStock?.Quantity || 0, -1, `${product.Brand} ${product.Model}`)}
                                          disabled={updating[`${product.Id}-MJTech`]}
                                          sx={{
                                            backgroundColor: 'error.main',
                                            color: '#1a237e',
                                            width: 32,
                                            height: 32,
                                            borderRadius: 2,
                                            border: '1px solid rgba(244, 67, 54, 0.3)',
                                            '&:hover': {
                                              backgroundColor: 'error.dark',
                                              transform: 'scale(1.05)',
                                              boxShadow: '0 4px 12px rgba(244, 67, 54, 0.3)',
                                            },
                                            transition: 'all 0.2s ease-in-out',
                                          }}
                                        >
                                          {updating[`${product.Id}-MJTech`] ? <CircularProgress size={14} color="inherit" /> : <RemoveIcon sx={{ fontSize: 16 }} />}
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                    <Tooltip title="Add 1 (Restock)">
                                      <IconButton
                                        size="small"
                                        onClick={() => confirmUpdateStock(product.Id, 'MJTech', mjtechStock?.Quantity || 0, 1, `${product.Brand} ${product.Model}`)}
                                        disabled={updating[`${product.Id}-MJTech`]}
                                        sx={{
                                          backgroundColor: 'success.main',
                                          color: '#1a237e',
                                          width: 32,
                                          height: 32,
                                          borderRadius: 2,
                                          border: '1px solid rgba(76, 175, 80, 0.3)',
                                          '&:hover': {
                                            backgroundColor: 'success.dark',
                                            transform: 'scale(1.05)',
                                            boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)',
                                          },
                                          transition: 'all 0.2s ease-in-out',
                                        }}
                                      >
                                        {updating[`${product.Id}-MJTech`] ? <CircularProgress size={14} color="inherit" /> : <AddIcon sx={{ fontSize: 16 }} />}
                                      </IconButton>
                                    </Tooltip>
                                    {mjtechStock && mjtechStock.Quantity > 0 && (
                                      <Tooltip title="Transfer 1 to Mobile Care">
                                        <IconButton
                                          size="small"
                                          onClick={() => confirmTransferStock(product.Id, 'MJTech', 'Mobile Care', 1, `${product.Brand} ${product.Model}`)}
                                          disabled={transferring[`${product.Id}-MJTech-Mobile Care`]}
                                          sx={{
                                            backgroundColor: 'info.main',
                                            color: '#1a237e',
                                            width: 32,
                                            height: 32,
                                            borderRadius: 2,
                                            border: '1px solid rgba(33, 150, 243, 0.3)',
                                            '&:hover': {
                                              backgroundColor: 'info.dark',
                                              transform: 'scale(1.05)',
                                              boxShadow: '0 4px 12px rgba(33, 150, 243, 0.3)',
                                            },
                                            transition: 'all 0.2s ease-in-out',
                                          }}
                                        >
                                          {transferring[`${product.Id}-MJTech-Mobile Care`] ? <CircularProgress size={14} color="inherit" /> : <TransferIcon sx={{ fontSize: 16 }} />}
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                    {mjtechStock && mjtechStock.Quantity > 0 && (
                                      <Tooltip title={`Record Sale (₱${getProductUnitPrice(product).toLocaleString()})`}>
                                        <IconButton
                                          size="small"
                                          onClick={() => confirmSaleStock(product.Id, 'MJTech', mjtechStock?.Quantity || 0, product)}
                                          disabled={selling[`${product.Id}-MJTech`]}
                                          sx={{
                                            backgroundColor: 'warning.main',
                                            color: '#1a237e',
                                            width: 32,
                                            height: 32,
                                            borderRadius: 2,
                                            border: '1px solid rgba(255, 152, 0, 0.3)',
                                            '&:hover': {
                                              backgroundColor: 'warning.dark',
                                              transform: 'scale(1.05)',
                                              boxShadow: '0 4px 12px rgba(255, 152, 0, 0.3)',
                                            },
                                            transition: 'all 0.2s ease-in-out',
                                          }}
                                        >
                                          {selling[`${product.Id}-MJTech`] ? <CircularProgress size={14} color="inherit" /> : <ShoppingCartIcon sx={{ fontSize: 16 }} />}
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                  </Box>
                                ) : null}
                              </Box>
                            </TableCell>
                            <TableCell align="center">
                              <Box sx={{ minWidth: 120, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                <Typography variant="h6" fontWeight={600}>
                                  {mobileCareStock?.Quantity || 0}
                                </Typography>
                                {/* Mobile Care Stock Actions */}
                                {canEdit() ? (
                                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                  {mobileCareStock && mobileCareStock.Quantity > 0 && (
                                    <Tooltip title="Subtract 1">
                                      <IconButton
                                        size="small"
                                        onClick={() => confirmUpdateStock(product.Id, 'Mobile Care', mobileCareStock?.Quantity || 0, -1, `${product.Brand} ${product.Model}`)}
                                        disabled={updating[`${product.Id}-Mobile Care`]}
                                        sx={{
                                          backgroundColor: 'error.main',
                                          color: '#1a237e',
                                          width: 32,
                                          height: 32,
                                          borderRadius: 2,
                                          border: '1px solid rgba(244, 67, 54, 0.3)',
                                          '&:hover': {
                                            backgroundColor: 'error.dark',
                                            transform: 'scale(1.05)',
                                            boxShadow: '0 4px 12px rgba(244, 67, 54, 0.3)',
                                          },
                                          transition: 'all 0.2s ease-in-out',
                                        }}
                                      >
                                        {updating[`${product.Id}-Mobile Care`] ? <CircularProgress size={14} color="inherit" /> : <RemoveIcon sx={{ fontSize: 16 }} />}
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                  <Tooltip title="Add 1 (Restock)">
                                    <IconButton
                                      size="small"
                                      onClick={() => confirmUpdateStock(product.Id, 'Mobile Care', mobileCareStock?.Quantity || 0, 1, `${product.Brand} ${product.Model}`)}
                                      disabled={updating[`${product.Id}-Mobile Care`]}
                                      sx={{
                                        backgroundColor: 'success.main',
                                        color: '#1a237e',
                                        width: 32,
                                        height: 32,
                                        borderRadius: 2,
                                        border: '1px solid rgba(76, 175, 80, 0.3)',
                                        '&:hover': {
                                          backgroundColor: 'success.dark',
                                          transform: 'scale(1.05)',
                                          boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)',
                                        },
                                        transition: 'all 0.2s ease-in-out',
                                      }}
                                    >
                                      {updating[`${product.Id}-Mobile Care`] ? <CircularProgress size={14} color="inherit" /> : <AddIcon sx={{ fontSize: 16 }} />}
                                    </IconButton>
                                  </Tooltip>
                                  {mobileCareStock && mobileCareStock.Quantity > 0 && (
                                    <Tooltip title="Transfer 1 to MJTech">
                                      <IconButton
                                        size="small"
                                        onClick={() => confirmTransferStock(product.Id, 'Mobile Care', 'MJTech', 1, `${product.Brand} ${product.Model}`)}
                                        disabled={transferring[`${product.Id}-Mobile Care-MJTech`]}
                                        sx={{
                                          backgroundColor: 'secondary.main',
                                          color: '#1a237e',
                                          width: 32,
                                          height: 32,
                                          borderRadius: 2,
                                          border: '1px solid rgba(156, 39, 176, 0.3)',
                                          '&:hover': {
                                            backgroundColor: 'secondary.dark',
                                            transform: 'scale(1.05)',
                                            boxShadow: '0 4px 12px rgba(156, 39, 176, 0.3)',
                                          },
                                          transition: 'all 0.2s ease-in-out',
                                        }}
                                      >
                                        {transferring[`${product.Id}-Mobile Care-MJTech`] ? <CircularProgress size={14} color="inherit" /> : <TransferIcon sx={{ fontSize: 16, transform: 'scaleX(-1)' }} />}
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                  {mobileCareStock && mobileCareStock.Quantity > 0 && (
                                    <Tooltip title={`Record Sale (₱${getProductUnitPrice(product).toLocaleString()})`}>
                                      <IconButton
                                        size="small"
                                        onClick={() => confirmSaleStock(product.Id, 'Mobile Care', mobileCareStock?.Quantity || 0, product)}
                                        disabled={selling[`${product.Id}-Mobile Care`]}
                                        sx={{
                                          backgroundColor: 'warning.main',
                                          color: '#1a237e',
                                          width: 32,
                                          height: 32,
                                          borderRadius: 2,
                                          border: '1px solid rgba(255, 152, 0, 0.3)',
                                          '&:hover': {
                                            backgroundColor: 'warning.dark',
                                            transform: 'scale(1.05)',
                                            boxShadow: '0 4px 12px rgba(255, 152, 0, 0.3)',
                                          },
                                          transition: 'all 0.2s ease-in-out',
                                        }}
                                      >
                                        {selling[`${product.Id}-Mobile Care`] ? <CircularProgress size={14} color="inherit" /> : <ShoppingCartIcon sx={{ fontSize: 16 }} />}
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                  </Box>
                                ) : null}
                              </Box>
                            </TableCell>
                            <TableCell align="center">
                              <Typography
                                variant="h5"
                                sx={{
                                  fontWeight: 700,
                                  color: stockStatus.color,
                                }}
                              >
                                {totalStock}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={total}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[10, 20, 50, 100]}
                  sx={{ borderTop: 1, borderColor: 'divider' }}
                />
              </CardContent>
            </Card>
          )}

          {/* Pagination for Mobile */}
          {isMobile && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 20, 50, 100]}
                sx={{
                  '& .MuiTablePagination-toolbar': {
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: 1, sm: 0 },
                  },
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  },
                }}
              />
            </Box>
          )}
        </>
      )}

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={confirmDialog.onCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: 'linear-gradient(145deg, #1A1F2E 0%, #2A2F3E 100%)',
            border: '1px solid rgba(0, 191, 255, 0.2)',
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle>
          {confirmDialog.title}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmDialog.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={confirmDialog.onCancel}
            variant="contained"
            color="primary"
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDialog.onConfirm}
            variant="contained"
            color="primary"
            autoFocus
          >
            Yes, Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Inventory;